<script setup lang="ts">
import { computed, ref, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'

/**
 * Champ date à la charte du site : saisie clavier dd/mm/YYYY + calendrier.
 * Contraintes via `min` (date minimale sélectionnable). La date du jour et le
 * passé sont exclus côté parent en passant min = demain.
 */
const props = defineProps<{
  modelValue: Date | null
  min?: Date | null
  max?: Date | null
  disabled?: boolean
  locale?: string
  id?: string
  placeholder?: string
  invalid?: boolean
  ariaLabel?: string
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: Date | null): void
  (e: 'blur'): void
}>()

function startOfDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}
function pad(n: number): string {
  return String(n).padStart(2, '0')
}
function format(d: Date): string {
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`
}
/** Parse strict dd/mm/YYYY → Date (rejette 31/02, etc.) ou null. */
function parse(str: string): Date | null {
  const m = str.trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!m) return null
  const day = Number(m[1])
  const month = Number(m[2])
  const year = Number(m[3])
  const d = new Date(year, month - 1, day)
  if (d.getFullYear() !== year || d.getMonth() !== month - 1 || d.getDate() !== day) return null
  return startOfDay(d)
}

const today = startOfDay(new Date())
const minDay = computed(() => (props.min ? startOfDay(props.min) : null))
const maxDay = computed(() => (props.max ? startOfDay(props.max) : null))
function isDisabled(d: Date): boolean {
  const t = startOfDay(d).getTime()
  if (minDay.value && t < minDay.value.getTime()) return true
  if (maxDay.value && t > maxDay.value.getTime()) return true
  return false
}

const text = ref(props.modelValue ? format(props.modelValue) : '')
const inputEl = ref<HTMLInputElement | null>(null)
const open = ref(false)
const viewDate = ref(startOfDay(props.modelValue ?? new Date()))

watch(
  () => props.modelValue,
  (val) => {
    // On ne resynchronise le texte que pour une date valide (clic calendrier /
    // valeur externe), jamais pendant une saisie partielle (val null).
    if (val) {
      const f = format(val)
      if (f !== text.value) text.value = f
      viewDate.value = startOfDay(val)
    }
  },
)

/** Position du curseur (index dans la chaîne formatée) juste après le n-ième
 *  chiffre, en sautant les « / » insérés par le masque. */
function caretAfterDigit(formatted: string, digitCount: number): number {
  if (digitCount <= 0) return 0
  let seen = 0
  for (let i = 0; i < formatted.length; i++) {
    if (formatted[i] >= '0' && formatted[i] <= '9') {
      seen++
      if (seen === digitCount) return i + 1
    }
  }
  return formatted.length
}

/** Masque progressif : on garde les chiffres et on insère les « / ». */
function onType(e: Event) {
  const el = e.target as HTMLInputElement
  // Nombre de chiffres avant le curseur : repère stable malgré la réécriture du
  // masque, pour replacer le curseur au bon endroit après reformatage.
  const caret = el.selectionStart ?? el.value.length
  const digitsBeforeCaret = el.value.slice(0, caret).replace(/\D/g, '').length

  const digits = el.value.replace(/\D/g, '').slice(0, 8)
  let out = digits
  if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
  else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`
  text.value = out

  // Réécrire `text` repositionne le curseur en fin de champ : on le restaure
  // après mise à jour du DOM, à la même frontière de chiffre que la saisie.
  const nextCaret = caretAfterDigit(out, digitsBeforeCaret)
  nextTick(() => {
    if (inputEl.value) inputEl.value.setSelectionRange(nextCaret, nextCaret)
  })

  const parsed = parse(out)
  emit('update:modelValue', parsed && !isDisabled(parsed) ? parsed : null)
}

function onBlur() {
  emit('blur')
}

function toggle() {
  if (props.disabled) return
  open.value = !open.value
  // À l'ouverture sans valeur, on se place sur le mois de la borne min (utile
  // pour un champ « fin » dont le min dépend du début choisi).
  if (open.value) viewDate.value = startOfDay(props.modelValue ?? props.min ?? new Date())
}

const viewYear = computed(() => viewDate.value.getFullYear())
const viewMonth = computed(() => viewDate.value.getMonth())

const monthLabel = computed(() =>
  new Intl.DateTimeFormat(props.locale ?? 'fr', { month: 'long', year: 'numeric' }).format(viewDate.value),
)

/** En-têtes jours, lundi en premier (1er janv. 2024 = lundi). */
const weekdays = computed(() => {
  const fmt = new Intl.DateTimeFormat(props.locale ?? 'fr', { weekday: 'short' })
  return Array.from({ length: 7 }, (_, i) => fmt.format(new Date(2024, 0, 1 + i)))
})

type Cell = { date: Date; disabled: boolean; isToday: boolean; selected: boolean }
const cells = computed<(Cell | null)[]>(() => {
  const first = new Date(viewYear.value, viewMonth.value, 1)
  const offset = (first.getDay() + 6) % 7 // décalage lundi-first
  const daysInMonth = new Date(viewYear.value, viewMonth.value + 1, 0).getDate()
  const out: (Cell | null)[] = []
  for (let i = 0; i < offset; i++) out.push(null)
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(viewYear.value, viewMonth.value, d)
    out.push({
      date,
      disabled: isDisabled(date),
      isToday: startOfDay(date).getTime() === today.getTime(),
      selected: !!props.modelValue && startOfDay(props.modelValue).getTime() === startOfDay(date).getTime(),
    })
  }
  return out
})

function prevMonth() {
  viewDate.value = new Date(viewYear.value, viewMonth.value - 1, 1)
}
function nextMonth() {
  viewDate.value = new Date(viewYear.value, viewMonth.value + 1, 1)
}

function pick(date: Date) {
  if (isDisabled(date)) return
  emit('update:modelValue', startOfDay(date))
  open.value = false
}

const root = ref<HTMLElement | null>(null)
function onDocClick(e: MouseEvent) {
  if (open.value && root.value && !root.value.contains(e.target as Node)) open.value = false
}
function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') open.value = false
}
onMounted(() => {
  document.addEventListener('click', onDocClick)
  document.addEventListener('keydown', onKeydown)
})
onBeforeUnmount(() => {
  document.removeEventListener('click', onDocClick)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <div ref="root" class="datefield" :class="{ 'datefield--invalid': invalid, 'datefield--disabled': disabled }">
    <div class="datefield__control">
      <input
        :id="id"
        ref="inputEl"
        :value="text"
        type="text"
        inputmode="numeric"
        autocomplete="off"
        class="datefield__input"
        :placeholder="placeholder ?? 'jj/mm/aaaa'"
        :aria-label="ariaLabel"
        :aria-invalid="invalid || undefined"
        :disabled="disabled"
        @input="onType"
        @blur="onBlur"
      />
      <button
        type="button"
        class="datefield__toggle"
        :aria-expanded="open"
        :aria-label="ariaLabel ? `${ariaLabel} — calendrier` : 'Ouvrir le calendrier'"
        :disabled="disabled"
        @click="toggle"
      >
        <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
          <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" fill="none" stroke="currentColor" stroke-width="1.7"/>
          <path d="M3.5 9.5h17M8 3.5v3M16 3.5v3" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round"/>
        </svg>
      </button>
    </div>

    <div v-if="open" class="datefield__pop" role="dialog" aria-label="Calendrier">
      <div class="datefield__pop-head">
        <button type="button" class="datefield__nav" aria-label="Mois précédent" @click="prevMonth">
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M12 5l-5 5 5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
        <span class="datefield__month">{{ monthLabel }}</span>
        <button type="button" class="datefield__nav" aria-label="Mois suivant" @click="nextMonth">
          <svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true">
            <path d="M8 5l5 5-5 5" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

      <div class="datefield__weekdays" aria-hidden="true">
        <span v-for="wd in weekdays" :key="wd">{{ wd }}</span>
      </div>

      <div class="datefield__grid" role="grid">
        <template v-for="(cell, i) in cells" :key="i">
          <span v-if="!cell" class="datefield__empty" aria-hidden="true"></span>
          <button
            v-else
            type="button"
            class="datefield__day"
            :class="{
              'is-today': cell.isToday,
              'is-selected': cell.selected,
            }"
            :disabled="cell.disabled"
            :aria-pressed="cell.selected"
            @click="pick(cell.date)"
          >
            {{ cell.date.getDate() }}
          </button>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.datefield {
  position: relative;
}

.datefield__control {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  background: var(--color-white);
  border: 1px solid var(--border-strong);
  border-radius: var(--radius-md);
  padding-right: 0.35rem;
  transition: border-color var(--dur-fast) var(--ease-out), box-shadow var(--dur-fast) var(--ease-out);
}
.datefield__control:focus-within {
  border-color: var(--primary);
  box-shadow: 0 0 0 3px var(--focus-ring);
}
.datefield--invalid .datefield__control {
  border-color: var(--danger);
}
.datefield--disabled .datefield__control {
  background: var(--bg-soft);
  opacity: 0.6;
}
.datefield--disabled .datefield__input,
.datefield--disabled .datefield__toggle {
  cursor: not-allowed;
}

.datefield__input {
  flex: 1;
  min-width: 0;
  padding: 0.7rem 0.9rem;
  background: none;
  border: 0;
  font: inherit;
  font-size: var(--text-base);
  color: var(--color-chocolate);
  letter-spacing: 0.02em;
}
.datefield__input:focus {
  outline: none;
}
.datefield__input::placeholder {
  color: var(--text-muted);
  opacity: 0.7;
}

.datefield__toggle {
  display: grid;
  place-items: center;
  width: 2.1rem;
  height: 2.1rem;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  color: var(--text-muted);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
}
.datefield__toggle:hover {
  background: var(--bg-soft);
  color: var(--primary);
}
.datefield__toggle:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.datefield__pop {
  position: absolute;
  z-index: var(--z-modal);
  top: calc(100% + 0.4rem);
  left: 0;
  width: min(20rem, 88vw);
  padding: 0.85rem;
  background: var(--color-white);
  border: 1px solid var(--border);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
}

.datefield__pop-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}
.datefield__month {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--color-chocolate);
  text-transform: capitalize;
}
.datefield__nav {
  display: grid;
  place-items: center;
  width: 2rem;
  height: 2rem;
  border: 0;
  border-radius: 50%;
  background: var(--bg-soft);
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
}
.datefield__nav:hover {
  background: var(--color-peach);
  color: var(--color-chocolate);
}
.datefield__nav:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.datefield__weekdays,
.datefield__grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0.15rem;
}
.datefield__weekdays {
  margin-bottom: 0.3rem;
}
.datefield__weekdays span {
  text-align: center;
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  padding: 0.2rem 0;
}

.datefield__empty {
  aspect-ratio: 1;
}
.datefield__day {
  display: grid;
  place-items: center;
  aspect-ratio: 1;
  border: 0;
  border-radius: var(--radius-sm);
  background: none;
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--color-chocolate);
  cursor: pointer;
  transition: background var(--dur-fast) var(--ease-out), color var(--dur-fast) var(--ease-out);
}
.datefield__day:hover {
  background: color-mix(in oklab, var(--color-peach) 30%, white);
}
.datefield__day:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 1px;
}
.datefield__day:disabled {
  color: var(--color-gray-500);
  cursor: not-allowed;
  background: none;
}
.datefield__day.is-today {
  box-shadow: inset 0 0 0 1.5px var(--primary);
  color: var(--primary);
}
.datefield__day.is-selected {
  background: var(--primary);
  color: var(--text-on-primary);
  box-shadow: none;
}
.datefield__day.is-selected:hover {
  background: var(--primary-hover);
}
</style>
